/**
 * Small hooks the whole console shares.
 *
 * Nothing here knows about the API. Data hooks live beside their pages, or in
 * `session.ts` for the two the shell owns.
 */
import type { RefObject } from 'react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

/** Debounce a value. Search inputs use this so a keystroke is not a request. */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs)
    return () => window.clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}

/**
 * Multi-select bookkeeping for a table with a header checkbox.
 *
 * Selection survives paging and filtering, which is why `selectedVisible` is
 * separate from `selected`: the header checkbox reflects the rows on screen,
 * while the action bar acts on everything picked.
 */
export function useSelection<T extends string>(allIds: readonly T[]) {
  const [selected, setSelected] = useState<ReadonlySet<T>>(() => new Set())

  const toggle = useCallback((id: T) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const setMany = useCallback((ids: readonly T[], on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      for (const id of ids) {
        if (on) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }, [])

  const clear = useCallback(() => setSelected(new Set()), [])

  const selectedVisible = useMemo(() => allIds.filter((id) => selected.has(id)), [allIds, selected])
  const allVisibleSelected = allIds.length > 0 && selectedVisible.length === allIds.length
  const someVisibleSelected = selectedVisible.length > 0 && !allVisibleSelected

  return {
    selected,
    selectedIds: Array.from(selected),
    selectedVisible,
    count: selected.size,
    toggle,
    setMany,
    clear,
    allVisibleSelected,
    someVisibleSelected,
  }
}

export interface Disclosure {
  isOpen: boolean
  open: () => void
  close: () => void
  toggle: () => void
  setOpen: (next: boolean) => void
}

/** Open and close state for modals, drawers and menus. */
export function useDisclosure(initial = false): Disclosure {
  const [isOpen, setOpen] = useState(initial)
  return useMemo(
    () => ({
      isOpen,
      open: () => setOpen(true),
      close: () => setOpen(false),
      toggle: () => setOpen((v) => !v),
      setOpen,
    }),
    [isOpen],
  )
}

/** Run a callback when Escape is pressed, while `enabled`. */
export function useOnEscape(enabled: boolean, handler: () => void): void {
  const saved = useRef(handler)
  saved.current = handler
  useEffect(() => {
    if (!enabled) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        saved.current()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [enabled])
}

/** Click or focus outside the element runs the handler. */
export function useOnClickOutside<T extends HTMLElement>(
  ref: RefObject<T>,
  enabled: boolean,
  handler: () => void,
): void {
  const saved = useRef(handler)
  saved.current = handler
  useEffect(() => {
    if (!enabled) return
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const node = ref.current
      if (node && event.target instanceof Node && !node.contains(event.target)) saved.current()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
    }
  }, [ref, enabled])
}

/** Stop the page behind an overlay from scrolling, without a layout jump. */
export function useLockBodyScroll(active: boolean): void {
  useLayoutEffect(() => {
    if (!active) return
    const { body } = document
    const previousOverflow = body.style.overflow
    const previousPadding = body.style.paddingRight
    const scrollbar = window.innerWidth - document.documentElement.clientWidth
    body.style.overflow = 'hidden'
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`
    return () => {
      body.style.overflow = previousOverflow
      body.style.paddingRight = previousPadding
    }
  }, [active])
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Keep Tab inside an overlay while it is open, and give focus back to whatever
 * had it when the overlay closes.
 */
export function useFocusTrap<T extends HTMLElement>(active: boolean): RefObject<T> {
  const containerRef = useRef<T>(null)

  useEffect(() => {
    if (!active) return
    const container = containerRef.current
    if (!container) return
    const previouslyFocused = document.activeElement as HTMLElement | null

    const focusables = () => Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE))
    const first = focusables()[0]
    ;(first ?? container).focus({ preventScroll: true })

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) {
        event.preventDefault()
        return
      }
      const firstItem = items[0]
      const lastItem = items[items.length - 1]
      const current = document.activeElement
      if (event.shiftKey && (current === firstItem || current === container)) {
        event.preventDefault()
        lastItem.focus()
      } else if (!event.shiftKey && current === lastItem) {
        event.preventDefault()
        firstItem.focus()
      }
    }

    container.addEventListener('keydown', onKeyDown)
    return () => {
      container.removeEventListener('keydown', onKeyDown)
      previouslyFocused?.focus?.({ preventScroll: true })
    }
  }, [active])

  return containerRef
}

/** Copy text and report success for a couple of seconds. */
export function useCopyToClipboard(resetMs = 2000): {
  copied: boolean
  copy: (text: string) => Promise<boolean>
} {
  const [copied, setCopied] = useState(false)
  const timer = useRef<number>()

  useEffect(() => () => window.clearTimeout(timer.current), [])

  const copy = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        window.clearTimeout(timer.current)
        timer.current = window.setTimeout(() => setCopied(false), resetMs)
        return true
      } catch {
        setCopied(false)
        return false
      }
    },
    [resetMs],
  )

  return { copied, copy }
}

/** Read a media query reactively. Used for the sidebar collapse breakpoint. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  )
  useEffect(() => {
    const list = window.matchMedia(query)
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches)
    setMatches(list.matches)
    list.addEventListener('change', onChange)
    return () => list.removeEventListener('change', onChange)
  }, [query])
  return matches
}

/** Persisted UI preference, safe when storage is unavailable. */
export function useLocalStorage<T>(key: string, initial: T): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key)
      return raw === null ? initial : (JSON.parse(raw) as T)
    } catch {
      return initial
    }
  })

  const set = useCallback(
    (next: T) => {
      setValue(next)
      try {
        window.localStorage.setItem(key, JSON.stringify(next))
      } catch {
        // A private window with storage blocked is not a reason to break the page.
      }
    },
    [key],
  )

  return [value, set]
}

export interface CursorPagination {
  /** Cursor to send with the next request, or undefined on the first page. */
  cursor: string | undefined
  /** Zero-based page index, for the "showing 21 to 40" line. */
  pageIndex: number
  canGoBack: boolean
  next: (nextCursor: string | null) => void
  back: () => void
  reset: () => void
}

/**
 * Cursor paging with a back button.
 *
 * The API hands out a forward cursor only, so going back means remembering the
 * cursors already used. That is what this keeps.
 */
export function useCursorPagination(resetKey?: unknown): CursorPagination {
  const [stack, setStack] = useState<(string | undefined)[]>([undefined])
  const firstRun = useRef(true)

  useEffect(() => {
    // The first render is already page one, so only a later filter change resets.
    if (firstRun.current) {
      firstRun.current = false
      return
    }
    setStack([undefined])
  }, [resetKey])

  return useMemo(
    () => ({
      cursor: stack[stack.length - 1],
      pageIndex: stack.length - 1,
      canGoBack: stack.length > 1,
      next: (nextCursor: string | null) => {
        if (!nextCursor) return
        setStack((current) => [...current, nextCursor])
      },
      back: () => setStack((current) => (current.length > 1 ? current.slice(0, -1) : current)),
      reset: () => setStack([undefined]),
    }),
    [stack],
  )
}

/** Set the browser tab title, restoring the previous one on unmount. */
export function useDocumentTitle(title: string | undefined): void {
  useEffect(() => {
    if (!title) return
    const previous = document.title
    document.title = `${title} · Son of Basel`
    return () => {
      document.title = previous
    }
  }, [title])
}

/** A stable id for pairing a label with its control. */
export function useStableId(prefix: string): string {
  const ref = useRef<string>()
  if (!ref.current) ref.current = `${prefix}-${Math.random().toString(36).slice(2, 9)}`
  return ref.current
}

/** Re-render on an interval. Relative timestamps use it to stay honest. */
export function useTicker(intervalMs = 30_000, enabled = true): number {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (!enabled) return
    const timer = window.setInterval(() => setTick((t) => t + 1), intervalMs)
    return () => window.clearInterval(timer)
  }, [intervalMs, enabled])
  return tick
}
